import React, { useMemo } from "react";
import { useCase } from "../../../../contexts/CaseContext";
import { Subheading } from "../../../../design/heading";
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../../design/table";
import UploadCaseFileButton from "./UploadCaseFileButton";
import CaseDetailFileListItem from "./CaseDetailFileListItem";
import FilesLearnMore from "../../../../components/LearnMores/FilesLearnMore";
import { Text } from "../../../../design/text";
import {
  FileCategory,
  FILE_CATEGORY_LABELS,
  FILE_CATEGORY_ORDER,
  ClientFile,
} from "@visitproof/types";
import {
  DocumentTextIcon,
  ScaleIcon,
  DocumentChartBarIcon,
  FolderIcon,
} from "@heroicons/react/24/outline";

interface CaseDetailFileListProps {
  caseId: string;
}

const CATEGORY_ICONS: Record<FileCategory, React.ElementType> = {
  contracts: DocumentTextIcon,
  court_paperwork: ScaleIcon,
  reports: DocumentChartBarIcon,
  misc: FolderIcon,
};

const CaseDetailFileList: React.FC<CaseDetailFileListProps> = ({ caseId }) => {
  const { files } = useCase();

  // Group files by category
  const filesByCategory = useMemo(() => {
    const grouped: Record<FileCategory, ClientFile[]> = {
      contracts: [],
      court_paperwork: [],
      reports: [],
      misc: [],
    };

    files.forEach((file) => {
      const category = file.category || "misc";
      grouped[category].push(file);
    });

    return grouped;
  }, [files]);

  // Get categories that have files
  const nonEmptyCategories = useMemo(() => {
    return FILE_CATEGORY_ORDER.filter(
      (category) => filesByCategory[category].length > 0
    );
  }, [filesByCategory]);

  return (
    <>
      <div className="flex flex-wrap items-center justify-between w-full">
        <div className="flex items-center gap-1">
          <Subheading>Files</Subheading>
          <FilesLearnMore type="case" />
        </div>
        <div className="flex gap-4">
          <UploadCaseFileButton caseId={caseId} />
        </div>
      </div>
      {files.length === 0 && (
        <div className="py-8 text-center">
          <p className="text-sm text-zinc-500 dark:text-zinc-400">No files have been uploaded yet.</p>
        </div>
      )}
      {files.length > 0 && (
        <div className="mt-4 space-y-6">
          {nonEmptyCategories.map((category) => {
            const Icon = CATEGORY_ICONS[category];
            const categoryFiles = filesByCategory[category];

            return (
              <div key={category}>
                <div className="flex items-center gap-2 mb-2">
                  <Icon className="h-5 w-5 text-zinc-500 dark:text-zinc-400" />
                  <Text className="font-medium text-zinc-900 dark:text-zinc-100">
                    {FILE_CATEGORY_LABELS[category]}
                  </Text>
                  <span className="text-xs text-zinc-400 dark:text-zinc-500">
                    ({categoryFiles.length})
                  </span>
                </div>
                <Table className="[--gutter:theme(spacing.6)] lg:[--gutter:theme(spacing.10)]">
                  <TableHead>
                    <TableRow>
                      <TableHeader>File</TableHeader>
                      <TableHeader>Description</TableHeader>
                      <TableHeader>Visible to Monitor</TableHeader>
                      <TableHeader className="text-right" />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {categoryFiles.map((file) => (
                      <CaseDetailFileListItem
                        key={file.id}
                        file={file}
                        caseId={caseId}
                      />
                    ))}
                  </TableBody>
                </Table>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
};

export default CaseDetailFileList;
